import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { formatarDataBR, hojeISO } from '../common/data';
import {
  COR_PRIORIDADE, LABEL_CATEGORIA, LABEL_PRIORIDADE, LABEL_STATUS,
} from './PainelInfraestrutura';

/**
 * Os chamados de manutenção da unidade.
 *
 * A lista vem ORDENADA PELO BACKEND, por urgência, e a tela não reordena. A
 * ordem não é cronológica de propósito: "portão da rua não tranca", aberto
 * hoje, precisa aparecer acima de "lâmpada queimada", aberta há três semanas.
 * Deixar o `<table>` ordenável por data de abertura convidaria justamente o
 * clique que desfaz isso.
 *
 * O histórico ao lado de cada chamado não é enfeite: chamado sem histórico é
 * boato. "Isso já foi resolvido" e "ninguém mexeu nisso" viram a palavra de um
 * contra a do outro assim que quem abriu sai de férias.
 */

interface Evento {
  id: number; tipo: string; de: string | null; para: string | null;
  anotacao: string | null; criado_em: string;
}

interface Patrimonio { id: number; item: string; local: string | null; estado: string; }

interface Chamado {
  id: number;
  titulo: string;
  descricao: string | null;
  categoria: string;
  local: string | null;
  prioridade: string;
  status: string;
  prazo: string | null;
  aberto_em: string;
  resolvido_em: string | null;
  responsavel: string | null;
  custo: number | null;
  solucao: string | null;
  id_patrimonio: number | null;
  id_vistoria_item: number | null;
  situacao: string;
  prazoTexto: string;
  aberto: boolean;
  eventos?: Evento[];
  patrimonio?: Patrimonio | null;
}

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 20,
};

const COR_SITUACAO: Record<string, string> = {
  atrasado: '#c62828', vence_hoje: '#e65100', no_prazo: '#2e7d32',
  sem_prazo: '#616161', encerrado: '#9e9e9e',
};

const PRIORIDADES = ['risco_seguranca', 'alta', 'media', 'baixa'];
const CATEGORIAS = ['eletrica', 'hidraulica', 'estrutural', 'mobiliario', 'equipamento', 'climatizacao', 'area_externa', 'outro'];
const STATUS = ['aberto', 'em_andamento', 'aguardando_terceiro', 'resolvido', 'cancelado'];

const NOVO_VAZIO = {
  titulo: '', descricao: '', categoria: 'outro', local: '',
  prioridade: 'media', prazo: '', responsavel: '', id_patrimonio: '',
};

const LABEL_EVENTO: Record<string, string> = {
  abertura: 'Abertura', status: 'Status', prioridade: 'Prioridade',
  prazo: 'Prazo', comentario: 'Comentário', encerramento: 'Encerramento',
};

export default function ChamadosSection({
  abrirId, aoMudar,
}: {
  abrirId?: number | null;
  aoMudar?: () => void;
}) {
  const [chamados, setChamados] = useState<Chamado[]>([]);
  const [patrimonio, setPatrimonio] = useState<Patrimonio[]>([]);
  const [aberto, setAberto] = useState<Chamado | null>(null);
  const [criando, setCriando] = useState(false);
  const [novo, setNovo] = useState({ ...NOVO_VAZIO });
  const [comentario, setComentario] = useState('');
  const [apenasAbertos, setApenasAbertos] = useState(true);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const mostrarErro = (e: any, padrao: string) => setErro(e?.response?.data?.message || padrao);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, bens] = await Promise.all([
        api.get<Chamado[]>('/infraestrutura/chamados', { params: { apenasAbertos: apenasAbertos ? 'true' : undefined } }),
        api.get<Patrimonio[]>('/infraestrutura/patrimonio'),
      ]);
      setChamados(lista.data);
      setPatrimonio(bens.data);
      setErro('');
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível carregar os chamados.');
    } finally {
      setCarregando(false);
    }
  }, [apenasAbertos]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrir = useCallback(async (id: number) => {
    try {
      const { data } = await api.get<Chamado>(`/infraestrutura/chamados/${id}`);
      setAberto(data);
      setErro('');
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível abrir o chamado.');
    }
  }, []);

  useEffect(() => { if (abrirId) abrir(abrirId); }, [abrirId, abrir]);

  async function criar() {
    if (!novo.titulo.trim()) { setErro('O chamado precisa de um título.'); return; }
    setSalvando(true);
    try {
      const { data } = await api.post<Chamado>('/infraestrutura/chamados', {
        ...novo,
        prazo: novo.prazo || undefined,
        id_patrimonio: novo.id_patrimonio || undefined,
      });
      setNovo({ ...NOVO_VAZIO });
      setCriando(false);
      await carregar();
      aoMudar?.();
      setAberto(data);
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível abrir o chamado.');
    } finally {
      setSalvando(false);
    }
  }

  async function alterar(id: number, campos: Record<string, any>) {
    setSalvando(true);
    try {
      const { data } = await api.put<Chamado>(`/infraestrutura/chamados/${id}`, campos);
      setAberto(data);
      await carregar();
      aoMudar?.();
      setErro('');
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível atualizar o chamado.');
    } finally {
      setSalvando(false);
    }
  }

  async function comentar(id: number) {
    const texto = comentario.trim();
    if (!texto) return;
    setSalvando(true);
    try {
      await api.post(`/infraestrutura/chamados/${id}/comentarios`, { anotacao: texto });
      setComentario('');
      await abrir(id);
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível registrar o comentário.');
    } finally {
      setSalvando(false);
    }
  }

  async function remover(id: number) {
    if (!window.confirm('Excluir este chamado? O histórico vai junto.')) return;
    try {
      await api.delete(`/infraestrutura/chamados/${id}`);
      setAberto(null);
      await carregar();
      aoMudar?.();
    } catch (e: any) {
      mostrarErro(e, 'Não foi possível excluir o chamado.');
    }
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <strong style={{ fontSize: 16 }}>Chamados de manutenção</strong>
        <label style={{ fontSize: 13, color: '#555', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={apenasAbertos} onChange={(e) => setApenasAbertos(e.target.checked)} />
          só os que ainda pedem alguma coisa
        </label>
        <button
          onClick={() => setCriando((v) => !v)}
          style={{
            marginLeft: 'auto', background: '#1565c0', color: '#fff', border: 'none',
            borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 600,
          }}
        >
          {criando ? 'Cancelar' : '+ Novo chamado'}
        </button>
      </div>

      {erro && <div style={{ color: '#c62828', marginBottom: 12 }}>{erro}</div>}

      {criando && (
        <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <label>O que está quebrado?
              <input value={novo.titulo} onChange={(e) => setNovo({ ...novo, titulo: e.target.value })}
                placeholder="Ex.: portão da entrada não tranca" style={{ width: '100%' }} />
            </label>
            <label>Onde
              <input value={novo.local} onChange={(e) => setNovo({ ...novo, local: e.target.value })}
                placeholder="Ex.: Entrada principal" style={{ width: '100%' }} />
            </label>
            <label>Categoria
              <select value={novo.categoria} onChange={(e) => setNovo({ ...novo, categoria: e.target.value })} style={{ width: '100%' }}>
                {CATEGORIAS.map((c) => <option key={c} value={c}>{LABEL_CATEGORIA[c]}</option>)}
              </select>
            </label>
            <label>Prioridade
              <select value={novo.prioridade} onChange={(e) => setNovo({ ...novo, prioridade: e.target.value })} style={{ width: '100%' }}>
                {PRIORIDADES.map((p) => <option key={p} value={p}>{LABEL_PRIORIDADE[p]}</option>)}
              </select>
            </label>
            <label>Prazo
              <input type="date" value={novo.prazo} onChange={(e) => setNovo({ ...novo, prazo: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label>Responsável
              <input value={novo.responsavel} onChange={(e) => setNovo({ ...novo, responsavel: e.target.value })}
                placeholder="Funcionário ou empresa" style={{ width: '100%' }} />
            </label>
            <label>Bem de patrimônio
              <select value={novo.id_patrimonio} onChange={(e) => setNovo({ ...novo, id_patrimonio: e.target.value })} style={{ width: '100%' }}>
                <option value="">— nenhum —</option>
                {patrimonio.map((b) => <option key={b.id} value={b.id}>{b.item}{b.local ? ` (${b.local})` : ''}</option>)}
              </select>
            </label>
          </div>
          <label style={{ display: 'block', marginTop: 10 }}>Detalhes
            <textarea value={novo.descricao} onChange={(e) => setNovo({ ...novo, descricao: e.target.value })}
              rows={2} style={{ width: '100%' }} />
          </label>
          <p style={{ color: '#777', fontSize: 12, margin: '8px 0 0' }}>
            Sem prazo informado, o sistema sugere um a partir da prioridade e grava no campo — a data fica
            visível e editável, em vez de existir só no cálculo.
          </p>
          <button onClick={criar} disabled={salvando}
            style={{ marginTop: 10, background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>
            {salvando ? 'Abrindo…' : 'Abrir chamado'}
          </button>
        </div>
      )}

      {carregando ? (
        <div style={{ color: '#666' }}>Carregando…</div>
      ) : chamados.length === 0 ? (
        <div style={{ color: '#666' }}>
          {apenasAbertos ? 'Nenhum chamado em aberto.' : 'Nenhum chamado registrado.'}
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {chamados.map((c) => (
            <li key={c.id} onClick={() => abrir(c.id)}
              style={{
                padding: '10px 0', borderTop: '1px solid #f0f0f0', cursor: 'pointer',
                display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
                background: aberto?.id === c.id ? '#f5faff' : undefined,
              }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: '#fff', background: COR_PRIORIDADE[c.prioridade],
                borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap',
              }}>
                {LABEL_PRIORIDADE[c.prioridade]}
              </span>
              <span style={{ fontWeight: 600 }}>{c.titulo}</span>
              <span style={{ color: '#777', fontSize: 12 }}>
                {LABEL_CATEGORIA[c.categoria]}{c.local ? ` · ${c.local}` : ''}
                {c.id_vistoria_item ? ' · veio de vistoria' : ''}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, color: '#555' }}>{LABEL_STATUS[c.status]}</span>
                <span style={{ color: COR_SITUACAO[c.situacao], fontSize: 13, fontWeight: 600, minWidth: 130, textAlign: 'right' }}>
                  {c.prazoTexto}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {aberto && (
        <div style={{ marginTop: 20, borderTop: '2px solid #eee', paddingTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>{aberto.titulo}</h3>
            <span style={{ color: '#777', fontSize: 13 }}>
              aberto em {formatarDataBR(aberto.aberto_em)}
              {aberto.resolvido_em && ` · resolvido em ${formatarDataBR(aberto.resolvido_em)}`}
            </span>
            <button onClick={() => setAberto(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>fechar ✕</button>
          </div>

          {aberto.descricao && <p style={{ color: '#444', marginTop: 8 }}>{aberto.descricao}</p>}
          {aberto.patrimonio && (
            <p style={{ color: '#555', fontSize: 13, margin: '4px 0' }}>
              Patrimônio: <strong>{aberto.patrimonio.item}</strong>
              {aberto.patrimonio.local ? ` (${aberto.patrimonio.local})` : ''} — estado atual: {aberto.patrimonio.estado}
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 12 }}>
            <label>Status
              <select value={aberto.status} disabled={salvando}
                onChange={(e) => alterar(aberto.id, { status: e.target.value })} style={{ width: '100%' }}>
                {STATUS.map((s) => <option key={s} value={s}>{LABEL_STATUS[s]}</option>)}
              </select>
            </label>
            <label>Prioridade
              <select value={aberto.prioridade} disabled={salvando}
                onChange={(e) => alterar(aberto.id, { prioridade: e.target.value })} style={{ width: '100%' }}>
                {PRIORIDADES.map((p) => <option key={p} value={p}>{LABEL_PRIORIDADE[p]}</option>)}
              </select>
            </label>
            <label>Prazo
              <input type="date" defaultValue={aberto.prazo ? String(aberto.prazo).slice(0, 10) : ''} disabled={salvando}
                onBlur={(e) => { if (e.target.value !== (aberto.prazo || '')) alterar(aberto.id, { prazo: e.target.value || null }); }}
                style={{ width: '100%' }} />
            </label>
            <label>Responsável
              <input defaultValue={aberto.responsavel || ''} disabled={salvando}
                onBlur={(e) => { if (e.target.value !== (aberto.responsavel || '')) alterar(aberto.id, { responsavel: e.target.value }); }}
                style={{ width: '100%' }} />
            </label>
            <label>Custo (R$)
              <input type="number" step="0.01" defaultValue={aberto.custo ?? ''} disabled={salvando}
                onBlur={(e) => alterar(aberto.id, { custo: e.target.value })} style={{ width: '100%' }} />
            </label>
          </div>

          <label style={{ display: 'block', marginTop: 10 }}>Como foi resolvido
            <textarea defaultValue={aberto.solucao || ''} rows={2} disabled={salvando}
              onBlur={(e) => { if (e.target.value !== (aberto.solucao || '')) alterar(aberto.id, { solucao: e.target.value }); }}
              style={{ width: '100%' }} />
          </label>

          <div style={{ marginTop: 16 }}>
            <strong style={{ fontSize: 13 }}>Histórico</strong>
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
              {(aberto.eventos || []).map((ev) => (
                <li key={ev.id} style={{ padding: '6px 0', borderTop: '1px solid #f5f5f5', fontSize: 13, color: '#444' }}>
                  <span style={{ color: '#888' }}>{formatarDataBR(ev.criado_em)}</span>{' · '}
                  <strong>{LABEL_EVENTO[ev.tipo] || ev.tipo}</strong>
                  {ev.de && ev.para && <> — de <em>{ev.de}</em> para <em>{ev.para}</em></>}
                  {!ev.de && ev.para && <> — {ev.para}</>}
                  {ev.anotacao && <div style={{ color: '#555' }}>{ev.anotacao}</div>}
                </li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input value={comentario} onChange={(e) => setComentario(e.target.value)}
                placeholder="Registrar uma anotação no histórico" style={{ flex: 1 }} />
              <button onClick={() => comentar(aberto.id)} disabled={salvando || !comentario.trim()}
                style={{ background: '#1565c0', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>
                Anotar
              </button>
            </div>
          </div>

          <button onClick={() => remover(aberto.id)}
            style={{ marginTop: 16, background: 'none', border: '1px solid #c62828', color: '#c62828', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
            Excluir chamado
          </button>
        </div>
      )}

      <p style={{ color: '#999', fontSize: 11, marginTop: 16, marginBottom: 0 }}>
        A lista vem ordenada por urgência do servidor: risco à segurança primeiro, depois o que passou do
        prazo. Hoje é {formatarDataBR(hojeISO())}.
      </p>
    </div>
  );
}
